declare const posthog: any;
declare const grecaptcha: any;
import { SimpleValidate } from './simple-validate'
import { getCreditUnionGroups } from './posthog-group'

type StandardLeadResponse = {
    success?: boolean
    content?: string
}

function resolveForm(trigger: HTMLElement): HTMLFormElement | null {
    const formId = trigger.getAttribute('data-formid')
    if (formId) {
        const formById = document.getElementById(formId)
        if (formById instanceof HTMLFormElement)
            return formById
    }

    if (trigger instanceof HTMLButtonElement && trigger.form)
        return trigger.form

    return trigger.closest('form')
}

function getSubmitButtons(form: HTMLFormElement): HTMLButtonElement[] {
    const buttons = new Set<HTMLButtonElement>()

    form.querySelectorAll<HTMLButtonElement>('.js-standard-lead-submission').forEach(button => buttons.add(button))

    const formId = form.getAttribute('id')
    if (formId) {
        document.querySelectorAll<HTMLButtonElement>(`.js-standard-lead-submission[data-formid="${formId}"]`).forEach(button => buttons.add(button))
    }

    return Array.from(buttons)
}

// Delegation also handles forms loaded into the contact modal after page load.
document.addEventListener('change', event => {
    const dropdown = event.target
    if (!(dropdown instanceof HTMLSelectElement) || !dropdown.matches('[data-lead-question]'))
        return
    const form = dropdown.closest('form')
    if (!form) return
    const option = dropdown.selectedOptions[0]
    const comments = form.querySelector<HTMLTextAreaElement>('textarea[name="Comments"]')
    if (comments) comments.value = option?.dataset.prefill ?? ''
    const label = option?.hasAttribute('data-written-price-request') ? 'Request Written Purchase Price' : 'Send Request'
    getSubmitButtons(form).forEach(button => {
        const text = button.querySelector<HTMLElement>('[data-submit-label]')
        if (text) text.textContent = label
    })
})

function setButtonPending(button: HTMLButtonElement, pending: boolean) {
    button.disabled = pending
    button.setAttribute('aria-busy', pending ? 'true' : 'false')

    const label = button.querySelector<HTMLElement>('[data-submit-label]')
    const pendingLabel = button.querySelector<HTMLElement>('[data-submit-pending]')

    label?.classList.toggle('hidden', pending)
    pendingLabel?.classList.toggle('hidden', !pending)
    pendingLabel?.classList.toggle('inline-flex', pending)
}

function setFormPending(form: HTMLFormElement, pending: boolean) {
    form.dataset.submitting = pending ? 'true' : 'false'
    getSubmitButtons(form).forEach(button => setButtonPending(button, pending))
}

const recaptchaSiteKey = '6LcJhpEqAAAAAOcTOmPz2Ob9U5Q6YzmDez5Ha_ZV'

// reCAPTCHA v3 scores what the visitor did up to the moment the token is made, and tokens expire
// after two minutes, so get one at submit rather than relying on the one fetched at page load.
async function refreshRecaptchaToken(form: HTMLFormElement) {
    const input = form.querySelector<HTMLInputElement>('input[name="recaptchaToken"]')
    if (!input || typeof grecaptcha === 'undefined')
        return

    try {
        input.value = await new Promise<string>((resolve, reject) =>
            grecaptcha.ready(() => grecaptcha.execute(recaptchaSiteKey, { action: 'submit' }).then(resolve, reject)))
    } catch {
        // Keep the page-load token; the server decides.
    }
}

function getFeedbackContainer(form: HTMLFormElement): HTMLElement | null {
    return form.querySelector<HTMLElement>('[data-standard-lead-feedback]')
}

function clearFeedback(form: HTMLFormElement) {
    const feedback = getFeedbackContainer(form)
    if (!feedback)
        return

    feedback.classList.add('hidden')
    feedback.innerHTML = ''
}

function showFeedback(form: HTMLFormElement, content: string) {
    const feedback = getFeedbackContainer(form)
    if (!feedback)
        return

    feedback.innerHTML = content
    feedback.classList.remove('hidden')
    feedback.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function handleSuccess(form: HTMLFormElement, content: string) {
    if (typeof posthog !== 'undefined') {
        const vehicleId = form.querySelector<HTMLInputElement>('input[name="InventoryVehicleId"]')?.value ?? undefined
        const dealershipId = form.querySelector<HTMLInputElement>('input[name="DealershipId"]')?.value ?? undefined

        posthog.capture('lead_form_submitted', {
            vehicle_id: vehicleId,
            dealership_id: dealershipId,
            $groups: getCreditUnionGroups(),
        })

        const email = form.querySelector<HTMLInputElement>('input[name="Email"], input[type="email"]')?.value?.trim()
        const firstName = form.querySelector<HTMLInputElement>('input[name="FirstName"]')?.value?.trim()
        const lastName = form.querySelector<HTMLInputElement>('input[name="LastName"]')?.value?.trim()
        if (email && email.length > 0) {
            posthog.identify(email, {
                firstName: firstName || undefined,
                lastName: lastName || undefined,
            })
        }
    }

    form.innerHTML = content

    document.getElementById('sl-modal-button-submit')?.classList.add('hidden')
    document.getElementById('sl-modal-button-cancel')?.classList.add('hidden')
    document.getElementById('sl-modal-button-close')?.classList.remove('hidden')
}

async function submitLeadForm(form: HTMLFormElement) {
    if (form.dataset.submitting === 'true')
        return

    const validator = SimpleValidate.getInstance(form)
    if (!validator.validate())
        return

    clearFeedback(form)
    setFormPending(form, true)

    try {
        await refreshRecaptchaToken(form)

        const response = await fetch('/?handler=StandardLeadSubmissionForm', {
            method: 'POST',
            body: new FormData(form)
        })

        const jsonData = await response.json() as StandardLeadResponse
        const content = jsonData.content ?? ''
        const isSuccess = response.ok && jsonData.success !== false

        if (isSuccess) {
            handleSuccess(form, content)
            return
        }

        showFeedback(form, content || '<div class="mt-4 text-red font-bold">The form could not be submitted. Please try again.</div>')
        if (typeof posthog !== 'undefined') {
            posthog.capture('lead_form_failed', { error_reason: 'server_error', $groups: getCreditUnionGroups() })
        }
        setFormPending(form, false)
    } catch {
        showFeedback(form, '<div class="mt-4 text-red font-bold">The form could not be submitted. Please try again.</div>')
        if (typeof posthog !== 'undefined') {
            posthog.capture('lead_form_failed', { error_reason: 'network_error', $groups: getCreditUnionGroups() })
        }
        setFormPending(form, false)
    }
}

document.addEventListener('click', function (event) {
    const trigger = (event.target as HTMLElement).closest('.js-standard-lead-submission') as HTMLButtonElement | null
    if (!trigger)
        return

    const form = resolveForm(trigger)
    if (!form || trigger.closest('form'))
        return

    event.preventDefault()
    void submitLeadForm(form)
})

document.addEventListener('submit', function (event) {
    const form = event.target as HTMLFormElement | null
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-standard-lead-form="true"]'))
        return

    event.preventDefault()
    void submitLeadForm(form)
})
